import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Users, Search, Loader2, ArrowLeft, Pencil, MapPin } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageUpload from "@/components/ImageUpload";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";

const HERO = "https://images.pexels.com/photos/1181605/pexels-photo-1181605.jpeg";

const Avatar = ({ name, avatar, size = 56 }) =>
  avatar ? (
    <img src={avatar} alt={name} className="rounded-full object-cover" style={{ height: size, width: size }} />
  ) : (
    <span className="grid place-items-center rounded-full bg-[#144F75]/10 text-[#144F75] font-semibold shrink-0" style={{ height: size, width: size, fontSize: size / 2.6 }}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </span>
  );

function ProfileEditor({ open, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", headline: user?.headline || "", bio: user?.bio || "",
    avatar: user?.avatar || "", location: user?.location || "",
    show_in_directory: user?.show_in_directory !== false,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/profile", form);
      toast.success("Profile updated");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const inp = "w-full rounded-lg border border-[#E5E0D8] px-3.5 py-2.5 text-sm outline-none focus:border-[#144F75] bg-white";
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-white" data-testid="profile-editor">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-[#144F75]">Edit your profile</SheetTitle>
          <SheetDescription>How you appear in the member directory.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" className={inp} /></div>
          <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Headline</label><input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. Breathwork facilitator" data-testid="profile-headline" className={inp} /></div>
          <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Location</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, State" data-testid="profile-location" className={inp} /></div>
          <div><ImageUpload value={form.avatar} onChange={(url) => setForm({ ...form, avatar: url })} label="Profile photo" rounded testid="profile-avatar" /></div>
          <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Bio</label><textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="profile-bio" className={inp} /></div>
          <label className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#1A242B]">Show me in the directory</span>
            <button type="button" onClick={() => setForm({ ...form, show_in_directory: !form.show_in_directory })} data-testid="profile-visibility"
              className={`relative h-7 w-12 rounded-full transition-colors ${form.show_in_directory ? "bg-[#144F75]" : "bg-[#E5E0D8]"}`}>
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${form.show_in_directory ? "left-6" : "left-1"}`} />
            </button>
          </label>
        </div>
        <SheetFooter className="pt-6">
          <button onClick={onClose} className="eyw-btn-ghost !py-2.5 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="profile-save" className="eyw-btn !py-2.5 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} Save</button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default function Members() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(false);
  const { data: members = [], isLoading, refetch } = useQuery({
    queryKey: ["members", q],
    queryFn: () => api.get(`/members?q=${encodeURIComponent(q)}`).then((r) => r.data),
  });

  return (
    <div data-testid="members-page">
      <PageHero label="Community" title="Member directory." subtitle="The people practicing their enhancements alongside you." image={HERO} testid="members-hero" />
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
          <Link to="/community" className="inline-flex items-center gap-1 text-sm text-[#4A5D6B] hover:text-[#144F75]"><ArrowLeft size={15} /> Community</Link>
          <div className="flex items-center gap-3 flex-1 justify-end">
            <div className="flex items-center gap-2 eyw-input max-w-xs w-full">
              <Search size={15} className="text-[#8FA89B]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" data-testid="members-search" className="bg-transparent outline-none flex-1 text-sm" />
            </div>
            {user && <button onClick={() => setEditing(true)} data-testid="edit-profile-btn" className="eyw-btn !py-2.5 text-sm"><Pencil size={15} /> Edit my profile</button>}
          </div>
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-20 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
        ) : members.length === 0 ? (
          <p className="text-center text-[#4A5D6B] py-20 flex flex-col items-center gap-3"><Users size={32} className="text-[#8FA89B]" /> No members found.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="members-grid">
            {members.map((m) => (
              <Link to={`/community/members/${m.id}`} key={m.id} data-testid={`member-${m.id}`} className="eyw-card p-5 flex gap-4 items-center hover:border-[#144F75] transition-colors">
                <Avatar name={m.name} avatar={m.avatar} />
                <div className="min-w-0">
                  <p className="font-semibold text-[#1A242B] truncate">{m.name}</p>
                  <p className="text-xs text-[#144F75] capitalize">{m.role}</p>
                  {m.headline && <p className="text-sm text-[#4A5D6B] truncate mt-0.5">{m.headline}</p>}
                  {m.location && <p className="text-xs text-[#8FA89B] flex items-center gap-1 mt-0.5"><MapPin size={11} /> {m.location}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <ProfileEditor open={editing} onClose={() => setEditing(false)} onSaved={refetch} />
    </div>
  );
}
